import React, { useRef } from 'react'
import { graphql, useStaticQuery } from 'gatsby'
import PropTypes from 'prop-types'
import { useTranslation, Trans } from 'gatsby-plugin-react-i18next'
import { GatsbyImage } from 'gatsby-plugin-image'

import { MostPopular, ProductSelectionHeaderRow } from 'src/templates/CalendarProductPage/styles'
import AddToCartButton from '../AddToCartButton'
import { Subheader, ButtonContainer } from '../FrameProductForm/styles'
import {
  Grid,
  SaveInfo,
  PriceAndExplainerContainer,
  ProductSelectionPricePoint,
  FistMonthFree,
  ProductSelectionBox,
  CalFeature,
  FeatureTextContainer,
  CalFeatureHeading,
  CalFeatureText,
  ClickableProductSelectionBox,
  ProductSelectionBoxWrapper,
} from './styles'
import { FRAME_PLUS_FEATURES, FRAME_NO_PLAN } from './constants'

const FramePickYourPrice = ({
  selectedProduct,
  setSelectedProduct,
  frameOriginalPrice,
  frameWithPlus,
  frameWithoutPlus,
  quantityVal,
  onAddToCart,
  overridenPrice,
  isOutOfStock,
}) => {
  const { t } = useTranslation('common')
  const { check, close, album, remote, cloud } = useStaticQuery(graphql`
    query FramePickYourPriceQuery {
      check: file(relativePath: { eq: "icons/check.webp" }) {
        childImageSharp {
          gatsbyImageData(layout: FIXED, width: 17, placeholder: NONE)
        }
      }
      close: file(relativePath: { eq: "icons/close.webp" }) {
        childImageSharp {
          gatsbyImageData(layout: FIXED, width: 15, placeholder: NONE)
        }
      }
      album: file(relativePath: { eq: "icons/album.webp" }) {
        childImageSharp {
          gatsbyImageData(layout: FIXED, width: 20, placeholder: NONE)
        }
      }
      remote: file(relativePath: { eq: "icons/remote.webp" }) {
        childImageSharp {
          gatsbyImageData(layout: FIXED, width: 25, placeholder: NONE)
        }
      }
      cloud: file(relativePath: { eq: "icons/cloud.webp" }) {
        childImageSharp {
          gatsbyImageData(layout: FIXED, width: 26, placeholder: NONE)
        }
      }
    }
  `)
  const calPlusFeatureIcons = { album, remote, cloud }
  const calNoPlusFeatureIcons = { check, close }
  let frameWithoutPlusPrice = frameWithoutPlus.variants[0].price
  const frameWithPlusPrice = frameWithPlus.variants[0].price
  const noplusSelectionRef = useRef(null)

  const handleSelectionScroll = (productType) => {
    setSelectedProduct(productType)
    setTimeout(() => {
      if (productType === 'standard' && noplusSelectionRef.current) {
        const elementPosition =
          noplusSelectionRef.current.getBoundingClientRect().top + window.scrollY
        const bannerElement = document.querySelector('.banner')
        const scrollOffset = bannerElement ? bannerElement.offsetHeight + 80 : 80

        window.scrollTo({
          top: elementPosition - scrollOffset,
          behavior: 'smooth',
        })
      }
    }, 0)
  }

  let displayedFrameOriginalPrice = frameOriginalPrice
  let displayedFrameWithPlusPrice = frameWithPlusPrice
  if (overridenPrice && overridenPrice.frameOriginalPrice) {
    displayedFrameOriginalPrice = overridenPrice.frameOriginalPrice
  }
  if (overridenPrice && overridenPrice.discountedPriceWithPlus) {
    displayedFrameWithPlusPrice = overridenPrice.discountedPriceWithPlus
  }
  const displayedStikethroughDesign = overridenPrice && overridenPrice.discountedPrice

  let displayedPriceSavings = (frameWithoutPlusPrice - displayedFrameWithPlusPrice).toFixed(2)
  if (displayedStikethroughDesign) {
    frameWithoutPlusPrice = overridenPrice.discountedPrice
    displayedPriceSavings = (displayedFrameOriginalPrice - displayedFrameWithPlusPrice).toFixed(2)
  }
  return (
    <>
      <Subheader>
        <Trans>Pick your price</Trans>
      </Subheader>
      {selectedProduct === 'standard' ? (
        <>
          <ProductSelectionBoxWrapper>
            <MostPopular>
              <Trans>Most Popular</Trans>
            </MostPopular>
            <ClickableProductSelectionBox
              popular
              style={{ marginTop: -16 }}
              onClick={() => {
                setSelectedProduct('with-plus')
              }}
            >
              <ProductSelectionHeaderRow>
                <SaveInfo>
                  <Trans
                    i18nKey="Save with Plus"
                    values={{
                      priceSaving: displayedPriceSavings,
                    }}
                  >
                    Save ${displayedPriceSavings} with Plus Plan
                  </Trans>
                </SaveInfo>
                <ProductSelectionPricePoint red>
                  <strike>${displayedFrameOriginalPrice}</strike>{' '}
                  <span>${displayedFrameWithPlusPrice}</span>
                </ProductSelectionPricePoint>
              </ProductSelectionHeaderRow>
            </ClickableProductSelectionBox>
          </ProductSelectionBoxWrapper>
          <ProductSelectionBox ref={noplusSelectionRef}>
            <ProductSelectionHeaderRow>
              <SaveInfo>
                <Trans>Purchase without Plus</Trans>
              </SaveInfo>
              {displayedFrameOriginalPrice === frameWithoutPlusPrice ? (
                <ProductSelectionPricePoint>${frameWithoutPlusPrice}</ProductSelectionPricePoint>
              ) : (
                <ProductSelectionPricePoint red>
                  <strike>${displayedFrameOriginalPrice}</strike>
                  <span>${frameWithoutPlusPrice}</span>
                </ProductSelectionPricePoint>
              )}
            </ProductSelectionHeaderRow>
            <Grid>
              {Object.keys(FRAME_NO_PLAN).map((key) => {
                const feature = FRAME_NO_PLAN[key]
                const icon = calNoPlusFeatureIcons[key]
                return (
                  <CalFeature key={feature.heading}>
                    <GatsbyImage image={icon.childImageSharp.gatsbyImageData} alt={feature.alt} />
                    <FeatureTextContainer>
                      <CalFeatureHeading>{feature.heading}</CalFeatureHeading>
                      <CalFeatureText>{feature.text}</CalFeatureText>
                    </FeatureTextContainer>
                  </CalFeature>
                )
              })}
            </Grid>
            <ButtonContainer fullWidth className="regular-cta">
              <AddToCartButton
                label={t(isOutOfStock ? 'Out of Stock' : 'Add to Cart')}
                handleClick={() => onAddToCart(frameWithoutPlus.variants[0].shopifyId, quantityVal)}
                oos={isOutOfStock}
              />
            </ButtonContainer>
          </ProductSelectionBox>
        </>
      ) : (
        <>
          <ProductSelectionBoxWrapper>
            <MostPopular>
              <Trans>Most Popular</Trans>
            </MostPopular>
            <ProductSelectionBox style={{ marginTop: -16 }} popular>
              <ProductSelectionHeaderRow>
                <SaveInfo>
                  <Trans
                    i18nKey="Save with Plus"
                    values={{
                      priceSaving: displayedPriceSavings,
                    }}
                  >
                    Save ${displayedPriceSavings} with Plus Plan
                  </Trans>
                </SaveInfo>
                <PriceAndExplainerContainer>
                  <ProductSelectionPricePoint>
                    <strike>${displayedFrameOriginalPrice}</strike>{' '}
                    <span>${displayedFrameWithPlusPrice}</span>
                  </ProductSelectionPricePoint>
                </PriceAndExplainerContainer>
              </ProductSelectionHeaderRow>
              <FistMonthFree>
                The first month of Frame Plus is free, then it's $39/year. This includes:
              </FistMonthFree>
              <Grid two>
                {Object.keys(FRAME_PLUS_FEATURES).map((key) => {
                  const feature = FRAME_PLUS_FEATURES[key]
                  const icon = calPlusFeatureIcons[key]
                  return (
                    <CalFeature key={feature.heading}>
                      <GatsbyImage image={icon.childImageSharp.gatsbyImageData} alt={feature.alt} />
                      <FeatureTextContainer>
                        <CalFeatureHeading>{feature.heading}</CalFeatureHeading>
                        <CalFeatureText>{feature.text}</CalFeatureText>
                      </FeatureTextContainer>
                    </CalFeature>
                  )
                })}
              </Grid>
              <ButtonContainer fullWidth className="regular-cta">
                <AddToCartButton
                  label={t(isOutOfStock ? 'Out of Stock' : 'Add to Cart')}
                  handleClick={() => onAddToCart(frameWithPlus.variants[0].shopifyId, quantityVal)}
                  oos={isOutOfStock}
                />
              </ButtonContainer>
            </ProductSelectionBox>
          </ProductSelectionBoxWrapper>
          <ClickableProductSelectionBox
            onClick={() => {
              handleSelectionScroll('standard')
            }}
          >
            <ProductSelectionHeaderRow>
              <SaveInfo>
                <Trans>Purchase without Plus</Trans>
              </SaveInfo>
              <PriceAndExplainerContainer>
                {displayedFrameOriginalPrice === frameWithoutPlusPrice &&
                !displayedStikethroughDesign ? (
                  <ProductSelectionPricePoint>${frameWithoutPlusPrice}</ProductSelectionPricePoint>
                ) : (
                  <ProductSelectionPricePoint>
                    <strike>${displayedFrameOriginalPrice}</strike>
                    <span>${frameWithoutPlusPrice}</span>
                  </ProductSelectionPricePoint>
                )}
              </PriceAndExplainerContainer>
            </ProductSelectionHeaderRow>
          </ClickableProductSelectionBox>
        </>
      )}
    </>
  )
}

FramePickYourPrice.propTypes = {
  selectedProduct: PropTypes.string.isRequired,
  setSelectedProduct: PropTypes.func.isRequired,
  frameOriginalPrice: PropTypes.string.isRequired,
  frameWithPlus: PropTypes.object.isRequired,
  frameWithoutPlus: PropTypes.object.isRequired,
  quantityVal: PropTypes.number,
  onAddToCart: PropTypes.func.isRequired,
  overridenPrice: PropTypes.object.isRequired,
  isOutOfStock: PropTypes.bool,
}

export default FramePickYourPrice
